import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import { Navigation, Thumbs } from "swiper/modules";
import { Link, useNavigate } from "react-router-dom";
import { ratingLabel } from "../utils/reviewStats";
const PropertyCard = ({ stay }) => {
  //. Hooks must always be called at the top
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const navigate = useNavigate();

  // If stay is null, return early
  if (!stay) return null;

  // homestay_average_rating defaults to "0.0" when a property has zero
  // approved reviews (ratings themselves are always 1-5, so 0 can only mean
  // "no reviews yet") — used to hide rating displays entirely rather than
  // show a misleading "0.0 stars".
  const hasReviews = Number(stay.homestay_average_rating) > 0;

  const mainImages = stay.galleries?.map((g) => g.image_url) || [];
  const thumbs = stay.galleries?.map((g) => g.image_url) || [];
  //. Compute minimum price across rooms
  const pricePerNight =
    stay.price_per_night ||
    Math.min(
      ...(stay.rooms?.map((r) => parseFloat(r.price_min)) || [0])
    ) ||
    0;

  const promoBadgeLabel = stay.promo_badge
    ? stay.promo_badge.discount_type === "percent"
      ? `${stay.promo_badge.discount_value}% OFF`
      : `₹${Math.round(stay.promo_badge.discount_value)} OFF`
    : null;

  return (
    <div className="propertylistbx">
      <div className="propertygallbx">
        <div className="thumbslider">
          {promoBadgeLabel && (
            <span className="promo-badge">
              <span className="promo-dot"></span>
              {promoBadgeLabel}
            </span>
          )}
          {hasReviews && (
            <span className="startp">
              <i className="fa fa-star"></i>
              <span>{stay.homestay_average_rating} stars</span>
            </span>
          )}

          {/* Main Swiper */}
          <Swiper
            modules={[Navigation, Thumbs]}
            navigation={false}
            loop
            thumbs={{ swiper: thumbsSwiper }}
            className="swiper main-slider"
          >
            {mainImages.length > 0 ? (
              mainImages.map((img, index) => (
                <SwiperSlide className="swiper-slide" key={index}>
                  <img
                    data-src={img}
                    src={img}
                    alt={`Property ${index + 1}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/property/${stay.id}`)}
                  />
                </SwiperSlide>
              ))
            ) : (
              <SwiperSlide>
                <img
                  src={stay.cover_image}
                  alt="Default Cover"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/property/${stay.id}`)}
                />
              </SwiperSlide>
            )}
          </Swiper>

          {/* Thumbnails Swiper */}
          <Swiper
            onSwiper={setThumbsSwiper}
            slidesPerView={4}
            spaceBetween={10}
            navigation={false}
            watchSlidesProgress
            modules={[Thumbs]}
            className="swiper thumb-slider"
          >
            {thumbs.map((img, index) => (
              <SwiperSlide className="swiper-slide" key={index}>
                <div className="thumbimg">
                  <img src={img} alt={`Thumb ${index + 1}`} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* Property Details */}
      <div className="propertycontents">
        <div className="propertytpbx">
          <div className="propertytplt">
            <h3><Link to={`/property/${stay.id}`} className="">
              {stay.title}
            </Link></h3>
            {stay.description !== null &&
              stay.description !== undefined &&
              stay.description !== "" &&
              stay.description !== "0" &&
              stay.description !== 0 && (
                <p className="proplistdesc">{stay.description}</p>
              )}
            {stay.activities &&
              stay.activities !== "0" &&
              stay.activities.trim() !== "" && (
                <ul>
                  {stay.activities
                    .split(",")
                    .map((activity, index) => (
                      <li key={index}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.11508 0.618164C3.92491 0.618164 0.515625 4.02744 0.515625 8.21762C0.515625 12.4078 3.92491 15.8171 8.11508 15.8171C12.3053 15.8171 15.7145 12.4078 15.7145 8.21762C15.7145 4.02744 12.3053 0.618164 8.11508 0.618164ZM12.3624 6.21776L7.5056 11.0365C7.21991 11.3222 6.7628 11.3412 6.45806 11.0555L3.88681 8.71282C3.58207 8.42713 3.56303 7.95097 3.82967 7.64623C4.11537 7.34149 4.59152 7.32245 4.89626 7.60814L6.93421 9.47467L11.2768 5.13213C11.5815 4.82739 12.0577 4.82739 12.3624 5.13213C12.6671 5.43687 12.6671 5.91302 12.3624 6.21776Z"
                            fill="#373758"
                          />
                        </svg>
                        <span>{activity.trim()}</span>
                      </li>
                    ))}
                </ul>
              )}

          </div>

          <div className="propertytprt">
            {hasReviews && (
              // #reviews — same deep-link hash PropertyDetails.jsx already
              // handles (opens the Reviews tab and scrolls to it), reused
              // here rather than a new mechanism.
              <Link to={`/property/${stay.id}#reviews`} className="cardratingsummary">
                <span className="cardratingsummary-text">
                  <span className="cardratingsummary-label">{ratingLabel(stay.homestay_average_rating)}</span>
                  {stay.review_count > 0 && (
                    <span className="cardratingsummary-count">
                      {stay.review_count} review{stay.review_count === 1 ? "" : "s"}
                    </span>
                  )}
                </span>
                <span className="cardratingsummary-score">{stay.homestay_average_rating}</span>
              </Link>
            )}
            <p className="text-nowrap">Price Per Night From</p>
            <div className="pricetxt">
              <span>₹ {stay.best_combination.total_price}</span>
            </div>
            {/* <a href="javascript:void(0);" className="theme-btn">
              Select Room
            </a> */}
            <Link to={`/property/${stay.id}`} className="theme-btn">
              Select Room
            </Link>
          </div>
        </div>

        {/* Amenities */}
        <div className="ammenties">
          {stay.amenities?.length > 0 ? (
            stay.amenities.map((a) => <span key={a.id}>{a.name}</span>)
          ) : (
            <span>No amenities listed</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;
